<div class="modal-header">
  <h4 class="modal-title" id="add-item-title">{{ isEditing ? 'Edit item' : 'Add item' }}</h4>
  <button type="button" class="btn-close" aria-label="Close" [disabled]="saving" (click)="cancel()"></button>
</div>
<form [formGroup]="form" (ngSubmit)="save()">
  <div class="modal-body" style="max-height: calc(100vh - 14rem); overflow-y: auto;">
    @if (errorMessage) {
      <div class="alert alert-danger" role="alert">{{ errorMessage }}</div>
    }
    @if (loading) {
      <div class="d-flex justify-content-center mb-3" role="status">
        <span class="spinner-border" aria-hidden="true"></span>
        <span class="visually-hidden">Loading item...</span>
      </div>
    }
    @if (loadFailed) {
      <button type="button" class="btn btn-outline-secondary mb-3" (click)="loadItem()">Retry</button>
    }
    <fieldset [disabled]="saving || loading || loadFailed">
      <legend class="h5">Item details</legend>
      <div class="row g-3">
        @for (field of itemFields; track field.key) {
          @if (field.key !== 'configuratorOrigin' && field.key !== 'pplCurrency' && (isEditing || (field.key !== 'replacedById' && field.key !== 'replacedByReason'))) {
            <div class="col-12 col-md-6 col-lg-4">
              <label class="form-label fw-bold" [for]="'item-' + field.key">{{ field.label }}{{ field.required ? ' *' : '' }}</label>
              @if (field.key === 'description' || field.key === 'detailedDescription') {
                <input type="text" class="form-control" [id]="'item-' + field.key" [formControlName]="field.key"
                  [attr.aria-required]="field.required || null"
                  [attr.aria-describedby]="form.controls[field.key].touched && form.controls[field.key].invalid ? 'item-' + field.key + '-error' : null"
                  [class.is-invalid]="form.controls[field.key].touched && form.controls[field.key].invalid" />
              } @else if (field.key === 'itemClassification') {
                <select class="form-select" [id]="'item-' + field.key" [formControlName]="field.key">
                  <option [ngValue]="null">-- Not selected --</option>
                  @for (option of itemClassificationOptions; track option.value) {
                    <option [value]="option.value">{{ option.text }}</option>
                  }
                </select>
              } @else {
                <input type="text" class="form-control" [id]="'item-' + field.key" [formControlName]="field.key"
                  [readonly]="isEditing && field.key === 'itemId'"
                  [attr.aria-required]="field.required || null"
                  [attr.aria-describedby]="form.controls[field.key].touched && form.controls[field.key].invalid ? 'item-' + field.key + '-error' : null"
                  [class.is-invalid]="form.controls[field.key].touched && form.controls[field.key].invalid" />
              }
              @if (form.controls[field.key].touched && form.controls[field.key].invalid) {
                <div class="invalid-feedback" [id]="'item-' + field.key + '-error'">{{ field.label }} is required.</div>
              }
            </div>
          }
        }
        <div class="col-12">
          <div class="row g-3 align-items-end">
            <div class="col-12 col-md-6 col-lg-4">
              <div class="form-check mb-2">
                <input id="item-isConfigured" type="checkbox" class="form-check-input" formControlName="isConfigured" />
                <label class="form-check-label fw-bold" for="item-isConfigured">Configured</label>
              </div>
            </div>
            <div class="col-12 col-md-6 col-lg-4">
              <label class="form-label fw-bold" for="item-configuratorOrigin">Configurator origin</label>
              <input id="item-configuratorOrigin" type="text" class="form-control" formControlName="configuratorOrigin" />
            </div>
          </div>
        </div>
      </div>
      <section class="mt-4" aria-labelledby="pricing-legacy-title">
        <h5 id="pricing-legacy-title" class="mb-3">Pricing Legacy</h5>
        <div class="row g-3">
          <div class="col-12 col-md-6 col-lg-4">
            <label class="form-label fw-bold" for="item-ppl">PPL</label>
            <input id="item-ppl" type="number" step="any" class="form-control" formControlName="ppl" />
          </div>
          <div class="col-12 col-md-6 col-lg-4">
            <label class="form-label fw-bold" for="item-pplCurrency">PPL currency</label>
            <input id="item-pplCurrency" type="text" class="form-control" formControlName="pplCurrency" />
          </div>
        </div>
      </section>
      <div class="d-flex justify-content-between align-items-center gap-2 mt-4 mb-3">
        <h5 class="mb-0">Suppliers ({{ suppliers.length }})</h5>
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="addSupplier()" [disabled]="saving">
          <i class="fa fa-plus" aria-hidden="true"></i> Add supplier
        </button>
      </div>
      <div formArrayName="supplierInsertRequestModel">
        <ul ngbNav #supplierNav="ngbNav" [(activeId)]="activeSupplierIndex" class="nav-tabs mb-3">
          @for (supplier of suppliers.controls; track supplier; let index = $index) {
            <li [ngbNavItem]="index" class="position-relative">
              <button ngbNavLink type="button" class="pe-5">{{ getSupplierTabLabel(supplier) }}</button>
              <button type="button" class="btn btn-sm border-0 position-absolute top-50 end-0 translate-middle-y px-2"
                title="Remove supplier" [attr.aria-label]="'Remove supplier ' + (index + 1)"
                [disabled]="saving || loading || loadFailed || supplier.controls.supplierId.disabled" (click)="$event.stopPropagation(); removeSupplier(index)">
                <i class="fa fa-xmark" aria-hidden="true"></i>
              </button>
              <ng-template ngbNavContent>
                <section [formGroupName]="index" class="pt-3 mb-4" [attr.aria-label]="'Supplier ' + (index + 1) + ' details'">
                  <div class="row g-3">
                    <div class="col-12 col-md-6 col-lg-4">
                      <label class="form-label fw-bold" [for]="'supplier-' + index + '-supplierId'">Supplier ID *</label>
                      <select class="form-select" [id]="'supplier-' + index + '-supplierId'" formControlName="supplierId"
                        aria-required="true"
                        [attr.aria-describedby]="supplier.controls.supplierId.touched && supplier.controls.supplierId.invalid ? 'supplier-' + index + '-supplierId-error' : null"
                        [class.is-invalid]="supplier.controls.supplierId.touched && supplier.controls.supplierId.invalid">
                        <option value="">-- Not selected --</option>
                        @if (supplier.controls.supplierId.disabled) {
                          <option [value]="supplier.controls.supplierId.value">{{ supplier.controls.supplierId.value }}</option>
                        }
                        @for (allowedSupplier of allowedSuppliers; track allowedSupplier.supplierId) {
                          <option [value]="allowedSupplier.supplierId" [disabled]="isSupplierSelectedElsewhere(index, allowedSupplier.supplierId)">
                            {{ allowedSupplier.supplierId }}{{ allowedSupplier.supplierName ? ' - ' + allowedSupplier.supplierName : '' }}
                          </option>
                        }
                      </select>
                      @if (supplier.controls.supplierId.touched && supplier.controls.supplierId.invalid) {
                        <div class="invalid-feedback" [id]="'supplier-' + index + '-supplierId-error'">Supplier ID is required.</div>
                      }
                    </div>
                    @for (required of [true, false]; track required) {
                      @for (field of supplierFields; track field.key) {
                        @if (!!field.required === required) {
                          <div class="col-12 col-md-6 col-lg-4">
                            <label class="form-label fw-bold" [for]="'supplier-' + index + '-' + field.key">{{ field.label }}{{ field.required ? ' *' : '' }}</label>
                            @if (field.key === 'hazardousClass') {
                              <select class="form-select" [id]="'supplier-' + index + '-' + field.key" [formControlName]="field.key">
                                <option value="">-- Not selected --</option>
                                @for (option of hazardousClassOptions; track option.value) {
                                  <option [value]="option.value">{{ option.text }}</option>
                                }
                              </select>
                            } @else {
                              <input type="text" class="form-control" [id]="'supplier-' + index + '-' + field.key" [formControlName]="field.key"
                                (blur)="field.key === 'leadTimeDuration' && normalizeLeadTimeDuration(supplier)"
                                [attr.aria-required]="field.required || null"
                                [attr.aria-describedby]="supplier.controls[field.key].touched && supplier.controls[field.key].invalid ? 'supplier-' + index + '-' + field.key + '-error' : null"
                                [class.is-invalid]="supplier.controls[field.key].touched && supplier.controls[field.key].invalid" />
                            }
                            @if (supplier.controls[field.key].touched && supplier.controls[field.key].invalid) {
                              <div class="invalid-feedback" [id]="'supplier-' + index + '-' + field.key + '-error'">{{ field.key === 'leadTimeDuration' && supplier.controls[field.key].hasError('pattern') ? 'Enter an ISO 8601 duration or a number of days.' : field.label + ' is required.' }}</div>
                            }
                          </div>
                        }
                      }
                      @if (required) {
                        <div class="col-12 col-md-6 col-lg-4">
                          <label class="form-label fw-bold" [for]="'supplier-' + index + '-stockCode'">Stock code *</label>
                          <select class="form-select" [id]="'supplier-' + index + '-stockCode'" formControlName="stockCode"
                            aria-required="true"
                            [attr.aria-describedby]="supplier.controls.stockCode.touched && supplier.controls.stockCode.invalid ? 'supplier-' + index + '-stockCode-error' : null"
                            [class.is-invalid]="supplier.controls.stockCode.touched && supplier.controls.stockCode.invalid">
                            <option value="">-- Not selected --</option>
                            @for (option of stockCodeOptions; track option.value) {
                              <option [value]="option.value">{{ option.text }}</option>
                            }
                          </select>
                          @if (supplier.controls.stockCode.touched && supplier.controls.stockCode.invalid) {
                            <div class="invalid-feedback" [id]="'supplier-' + index + '-stockCode-error'">Stock code is required.</div>
                          }
                        </div>
                      }
                      @for (field of supplierNumberFields; track field.key) {
                        @if (field.required === required) {
                          <div class="col-12 col-md-6 col-lg-4">
                            <label class="form-label fw-bold" [for]="'supplier-' + index + '-' + field.key">{{ field.label }}{{ field.required ? ' *' : '' }}</label>
                            <input type="number" step="any" class="form-control" [id]="'supplier-' + index + '-' + field.key" [formControlName]="field.key"
                              [attr.aria-required]="field.required || null"
                              [attr.aria-describedby]="supplier.controls[field.key].touched && supplier.controls[field.key].invalid ? 'supplier-' + index + '-' + field.key + '-error' : null"
                              [class.is-invalid]="supplier.controls[field.key].touched && supplier.controls[field.key].invalid" />
                            @if (supplier.controls[field.key].touched && supplier.controls[field.key].invalid) {
                              <div class="invalid-feedback" [id]="'supplier-' + index + '-' + field.key + '-error'">{{ supplier.controls[field.key].hasError('min') ? field.label + ' must be greater than 0.' : field.label + ' is required.' }}</div>
                            }
                          </div>
                        }
                      }
                    }
                    <div class="col-12">
                      <div class="form-check">
                        <input type="checkbox" class="form-check-input" [id]="'supplier-main-' + index" formControlName="isMainSupplier"
                          (change)="setMainSupplier(index, $any($event.target).checked)" />
                        <label class="form-check-label fw-bold" [for]="'supplier-main-' + index">Main supplier</label>
                      </div>
                    </div>
                  </div>
                </section>
              </ng-template>
            </li>
          }
        </ul>
        <div [ngbNavOutlet]="supplierNav"></div>
      </div>
    </fieldset>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" [disabled]="saving" (click)="cancel()">Cancel</button>
    <button type="submit" class="btn btn-primary" [disabled]="saving || loading || loadFailed || form.invalid">
      @if (saving) {
        <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
      } @else {
        <i [class]="isEditing ? 'fa fa-save me-1' : 'fa fa-plus me-1'" aria-hidden="true"></i>
      }
      {{ saving ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save changes' : 'Add item') }}
    </button>
  </div>
</form>